import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, FileText, Mail } from "lucide-react";
import ThemeToggle from "../common/ThemeToggle";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      {/* Navigation */}
      <nav className="fixed right-0 left-0 top-0 shadow-lg bg-white dark:bg-slate-900/95 backdrop-blur-lg border-b border-gray-100 dark:border-gray-800 transition-all duration-300 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-20 gap-8">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/usiu-logo-02.png"
                alt="USIU Logo"
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  e.target.src = "/usiu-logo.png";
                }}
              />
              <div>
                <h1 className="text-xl font-display font-bold bg-gradient-usiu-primary bg-clip-text text-transparent">
                  USIU-Africa
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">Counseling & Wellbeing</p>
              </div>
            </Link>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Link
                to="/"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-200 font-medium px-4 py-2 rounded-2xl hover:bg-usiu-blue-50 dark:hover:bg-slate-800 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-block mb-4">
              <span className="bg-usiu-blue-100 dark:bg-usiu-blue-900/30 text-usiu-blue-700 dark:text-usiu-blue-300 px-4 py-2 rounded-full text-sm font-semibold">
                Legal
              </span>
            </div>
            <h1 className="text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Last updated: January 19, 2026
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 dark:border-blue-400 p-6 rounded-r-lg mb-8">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  USIU-Africa Counseling Center is committed to protecting your privacy and maintaining the confidentiality of your personal and health information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our counseling services and platform.
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Information We Collect
                </h2>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Personal Information</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Full name, student/staff ID, email address, and phone number</li>
                    <li>Date of birth and demographic information</li>
                    <li>Emergency contact information</li>
                    <li>Academic information (for students)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Health Information</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Mental health history and current concerns</li>
                    <li>Session notes and treatment plans</li>
                    <li>Assessment results and diagnoses</li>
                    <li>Medication information (if applicable)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Usage Information</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Appointment scheduling and attendance records</li>
                    <li>Platform usage data and preferences</li>
                    <li>Communication history with counselors</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 dark:bg-green-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  How We Use Your Information
                </h2>
              </div>

              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <span>To provide mental health counseling services and support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <span>To schedule and manage counseling appointments</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <span>To maintain accurate health records and treatment documentation</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <span>To communicate with you about your appointments and care</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <span>To improve our services and platform functionality</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                  <span>To comply with legal and regulatory requirements</span>
                </li>
              </ul>
            </section>

            {/* Information Security */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 dark:bg-purple-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Information Security
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  We implement industry-standard security measures to protect your personal and health information:
                </p>
                <ul className="space-y-2">
                  <li>End-to-end encryption for all communications</li>
                  <li>Secure, encrypted storage of all health records</li>
                  <li>Regular security audits and updates</li>
                  <li>Restricted access to authorized personnel only</li>
                  <li>Multi-factor authentication for platform access</li>
                  <li>Regular staff training on confidentiality and data protection</li>
                </ul>
              </div>
            </section>

            {/* Confidentiality and Disclosure */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 dark:bg-orange-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Confidentiality and Disclosure
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  Your counseling sessions and health information are strictly confidential. We will not disclose your information without your written consent, except in the following circumstances:
                </p>
                <ul className="space-y-2">
                  <li><strong>Imminent Danger:</strong> If there is a serious threat of harm to yourself or others</li>
                  <li><strong>Child or Elder Abuse:</strong> If we suspect abuse or neglect of a minor or vulnerable adult</li>
                  <li><strong>Legal Requirements:</strong> When required by law or court order</li>
                  <li><strong>Emergency Situations:</strong> To protect your vital interests in medical emergencies</li>
                </ul>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Your Rights
              </h2>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>You have the right to:</p>
                <ul className="space-y-2">
                  <li>Access your health records and personal information</li>
                  <li>Request corrections to inaccurate information</li>
                  <li>Request restrictions on the use of your information</li>
                  <li>Receive a copy of this Privacy Policy</li>
                  <li>File a complaint if you believe your privacy rights have been violated</li>
                  <li>Withdraw consent for certain uses of your information</li>
                </ul>
              </div>
            </section>

            {/* Data Retention */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Data Retention
              </h2>

              <p className="text-gray-700 dark:text-gray-300">
                We retain your health records and personal information for a minimum of 7 years after your last session, or as required by law. After this period, records are securely destroyed in accordance with professional and legal standards.
              </p>
            </section>

            {/* Contact Us */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-teal-100 dark:bg-teal-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Contact Us
                </h2>
              </div>

              <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-6 space-y-3">
                <p className="text-gray-700 dark:text-gray-300">
                  If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
                </p>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p><strong>Email:</strong> counsel@usiu.ac.ke</p>
                  <p><strong>Phone:</strong> +254 730 116 748</p>
                  <p><strong>Address:</strong> USIU-Africa, Thika Road, Kasarani, P.O. Box 14634-00800, Nairobi, Kenya</p>
                </div>
              </div>
            </section>

            {/* Updates to Policy */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Updates to This Policy
              </h2>

              <p className="text-gray-700 dark:text-gray-300">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on our platform and updating the "Last updated" date. Your continued use of our services after such changes constitutes your acceptance of the updated policy.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-slate-950 text-white py-8 px-6 border-t border-gray-800 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} USIU-Africa Counseling Center. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
